import ReactECharts from 'echarts-for-react';
import { backendURL } from "../Globals";
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Col, Row, Statistic } from 'antd';

const ReportsComponent = () => {
  const [articles, setArticles] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [editores, setEditores] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [todos, setTodos] = useState("");
  const [jcrqData, setJcrqData] = useState([]);
  const [sjrqData, setSjrqData] = useState([]);
  const [publisherData, setPublisherData] = useState([]);
  const [businessModelData, setBusinessModelData] = useState([]);
  const [totalJournals, setTotalJournals] = useState("");

  const handleError = (error) => {
    alert(error);
  };

  useEffect(() => {
    const urls = [
      `${backendURL}/analytics/total`,
      `${backendURL}/analytics/rama`,
      `${backendURL}/analytics/departamento`,
      `${backendURL}/analytics/editor`,
      `${backendURL}/analytics/periodo`,
      `${backendURL}/analytics/jcrq`,
      `${backendURL}/analytics/sjrq`,
      `${backendURL}/analytics/publisher`,
      `${backendURL}/analytics/business-model`,
      `${backendURL}/analytics/total-journals`
    ];

    Promise.all(urls.map(url => fetch(url).then(res => res.json())))
      .then(([
        totalData, ramaData, departamentoData, editorData, periodoData,
        jcrqData, sjrqData, publisherData, businessModelData, totalJournalsData
      ]) => {
        setTodos(totalData[0]?.cantidad || "0");
        setArticles(ramaData);
        setDepartamentos(departamentoData);
        setEditores(editorData);
        setPeriodos(periodoData);
        setJcrqData(jcrqData);
        setSjrqData(sjrqData);
        setPublisherData(publisherData);
        setBusinessModelData(businessModelData);
        setTotalJournals(totalJournalsData[0]?.cantidad || "0");
      })
      .catch(error => {
        console.error("Fetch error: ", error);
        handleError("Error fetching data");
      });
  }, []);

  const pieChartConfig = useMemo(() => ({
    tooltip: { trigger: 'item' },
    legend: { top: '5%', left: 'center' },
    seriesDefaults: {
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: { show: false, position: 'center' },
      emphasis: {
        label: {
          show: true,
          fontSize: '40',
          fontWeight: 'bold'
        }
      },
      labelLine: { show: false }
    }
  }), []);

  const jcrq = useMemo(() => ({
    ...pieChartConfig,
    series: [
      {
        ...pieChartConfig.seriesDefaults,
        name: 'JCR Quartiles',
        data: jcrqData.map(item => ({
          value: item.cantidad,
          name: item.jcrq
        }))
      }
    ]
  }), [jcrqData, pieChartConfig]);

  const sjrq = useMemo(() => ({
    ...pieChartConfig,
    series: [
      {
        ...pieChartConfig.seriesDefaults,
        name: 'SJR Quartiles',
        data: sjrqData.map(item => ({
          value: item.cantidad,
          name: item.sjrq
        }))
      }
    ]
  }), [sjrqData, pieChartConfig]);

  const publisher = useMemo(() => ({
    ...pieChartConfig,
    series: [
      {
        ...pieChartConfig.seriesDefaults,
        name: 'Publishers',
        data: publisherData.map(item => ({
          value: item.cantidad,
          name: item.publisher
        }))
      }
    ]
  }), [publisherData, pieChartConfig]);

  const businessModel = useMemo(() => ({
    ...pieChartConfig,
    series: [
      {
        ...pieChartConfig.seriesDefaults,
        name: 'Business Models',
        data: businessModelData.map(item => ({
          value: item.cantidad,
          name: item['business model']
        }))
      }
    ]
  }), [businessModelData, pieChartConfig]);

  const barChartConfig = (data, xAxisData) => ({
    grid: {
      left: '10%',
      right: '10%',
      bottom: '40%', // Aumenta el margen inferior para dar más espacio a las etiquetas
      top: '10%'
    },
    tooltip: {
      trigger: 'axis', // Mostrar el tooltip al pasar por el eje
      axisPointer: { // Eje al que se refiere el tooltip
        type: 'shadow' // Muestra una sombra en el eje Y al pasar el cursor
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)', // Fondo blanco con transparencia
      borderColor: '#ccc',
      borderWidth: 1,
      textStyle: {
        color: '#000',
        fontSize: 12
      },
      formatter: function (params) {
        const param = params[0];
        return `<div style="padding: 5px; font-family: Arial, sans-serif;">
                  <span>${param.name}</span><br/>
                  <span style="font-size: 14px; font-weight: bold;">${param.value}</span>
                </div>`;
      }
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        show: true,
        interval: 0,
        rotate: 45,  // Mantén la rotación a 45 grados
        fontSize: 12, // Ajusta el tamaño de la fuente según sea necesario
        formatter: function (value) {
          return value.length > 10 ? value.slice(0, 15) + '...' : value; // Corta nombres muy largos, opcional
        }
      }
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        data: data,
        type: 'bar'
      }
    ]
  });

  const rama = useMemo(() => barChartConfig(articles.map(item => item.cantidad), articles.map(item => item.ramaDepartamento)), [articles]);
  const departamento = useMemo(() => barChartConfig(departamentos.map(item => item.cantidad), departamentos.map(item => item.departamento)), [departamentos]);
  const editor = useMemo(() => barChartConfig(editores.map(item => item.cantidad), editores.map(item => item.editor)), [editores]);
  const periodo = useMemo(() => barChartConfig(periodos.map(item => item.cantidad), periodos.map(item => item.periodo)), [periodos]);

  const cardStyle = {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Añade una sombra notable
    borderRadius: '10px', // Añade bordes redondeados para un mejor efecto
    padding: '20px', // Añade más margen interno (padding)
    marginBottom: '1cm', // Espaciado inferior entre las cards
    height: '100%', // Asegura que todas las cards tengan la misma altura
  };

  const statisticCardStyle = {
    ...cardStyle,
    width: 'fit-content', // Ajusta el ancho de las cards de los Statistic para que sea corto
    maxWidth: '300px', // Limita el ancho máximo para evitar que sea demasiado ancho
    textAlign: 'center', // Centra el contenido en la card
    margin: '0 auto' // Centra la card en el contenedor
  };

  const cardTitleStyle = {
    fontSize: '20px', // Fuente más grande
    fontWeight: 'normal', // Elimina la negrita
    color: '#555', // Un tono gris oscuro para el título
    textAlign: 'center',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", // Tipografía más moderna
    letterSpacing: '0.5px' // Espaciado de letras para mejor legibilidad
  };

  return (
    <Row style={{ padding: '20px', paddingTop: '2cm' }} gutter={[24, 24]} justify="center">
  
  <Col span={24}>
  <h2 className="section-title">Revistas elegibles: {totalJournals}</h2>
</Col>

  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Distribución de revistas por cuartil JCR</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={jcrq} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Distribución de revistas por cuartil SJR</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={sjrq} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Distribución de revistas por editor</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={publisher} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Distribución de revistas por modelo de negocio</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={businessModel} />
    </Card>
  </Col>

  <Col span={24}>
  <h2 className="section-title">Artículos financiados: {todos}</h2>
</Col>


  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Artículos aprobados por ramas del conocimiento</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={rama} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Artículos aprobados por departamentos uniovi</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={departamento} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Artículos aprobados por editor</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={editor} />
    </Card>
  </Col>
  
  <Col span={24} md={12}>
    <Card title={<span style={cardTitleStyle}>Artículos aprobados por periodo</span>} bordered={false} className="chart-card" style={cardStyle}>
      <ReactECharts option={periodo} />
    </Card>
  </Col>
</Row>

  );
};

export default ReportsComponent;









